import React, { useContext } from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';

import { storeContext } from '../../context';
import {
  eventNames,
  paymentStartPropertyNames,
  paymentTypes
} from '../../../constants/mixpanel';
import * as PropTypes from 'prop-types';
import SystemButton from './SystemButton';

const StyledButton = styled(SystemButton)`
  && {
    a {
      color: white;
      &:hover {
        text-decoration: none;
      }
    }
  }
`;

const PaymentButton = observer(
  ({
    children,
    onClick,
    paymentButtonType,
    sourceIsMessagesCenter,
    billStatus,
    variant,
    color,
    ...props
  }) => {
    const {
      addressDetailsModel,
      payable,
      curaeModel,
      tracking,
      router: { history },
      userState
    } = useContext(storeContext);

    const { PAYMENT_START } = eventNames;

    const {
      PP_CONVERT,
      PP_UPDATE_PAYMENT_METHOD,
      NOTIFICATION_BANNER,
      PAY_FULL_PLAN,
      PAYMENT,
      ASSIST_PP,
      ADD_OUTSIDE_BALANCE,
      PP_BRING_CURRENT,
      CATCH_UP_PROMPT,
      NOTIFICATION_BANNER_CATCH_UP,
      FINANCING_PP,
      ONE_CLICK_PAYMENT
    } = paymentTypes;

    const {
      BILLS_ON_PAYMENT_PLAN,
      BILL_DETAILS_VIEWED,
      PAYMENT_ENTRANCE,
      MESSAGE_CENTER,
      MESSAGE_CENTER_BILL_STATUS,
      CONVERT_PLAN,
      UPDATE_PAYMENT_PLAN
    } = paymentStartPropertyNames;

    const getNextRoute = async () => {
      const isFinancedOffer =
        paymentButtonType === FINANCING_PP && payable.hasFinancedLightOffer;

      if (isFinancedOffer && !addressDetailsModel?.dataApplied) {
        await addressDetailsModel.load();
      }

      return isFinancedOffer ? '/underwriting_data' : '/payment';
    };

    const trackPaymentStart = () => {
      const hasConvertPlan =
        payable.paymentPlan?.planType === 'manual' &&
        [PP_CONVERT, PP_UPDATE_PAYMENT_METHOD, NOTIFICATION_BANNER].includes(
          paymentButtonType
        );

      tracking.track(PAYMENT_START, {
        [BILL_DETAILS_VIEWED]: payable.billDetailsViewed,
        [BILLS_ON_PAYMENT_PLAN]: payable.billsOnPaymentPlan,
        [PAYMENT_ENTRANCE]: paymentButtonType,
        ...(hasConvertPlan && { [CONVERT_PLAN]: true }),
        ...(payable.paymentPlan &&
          payable.paymentPlan.planType !== 'manual' && {
            [UPDATE_PAYMENT_PLAN]: true
          }),
        ...(!!sourceIsMessagesCenter && {
          [MESSAGE_CENTER]: !!sourceIsMessagesCenter,
          [MESSAGE_CENTER_BILL_STATUS]: billStatus
        })
      });
    };

    const onPaymentClick = async e => {
      e.preventDefault();
      onClick && onClick(e);
      userState.set('selectedPaymentType', paymentButtonType);
      userState.set(
        'paymentAmount',
        payable.getTotalAmountByPaymentType(paymentButtonType)
      );
      // when bringing account current the only bills that should be selected are the pp bills
      if (
        [
          PP_BRING_CURRENT,
          CATCH_UP_PROMPT,
          NOTIFICATION_BANNER_CATCH_UP
        ].includes(paymentButtonType) &&
        payable.selectBills
      )
        payable.setSelectAllBills(false);
      // when adding outside balance, sab is not relevant
      if (paymentButtonType === ADD_OUTSIDE_BALANCE && payable.selectBills)
        payable.setSelectAllBills(true, false);
      if (
        payable.isCuraeOn &&
        (!payable.hasPaymentPlan || paymentButtonType === PAY_FULL_PLAN)
      )
        await curaeModel.fetchData(userState.paymentAmount);

      trackPaymentStart();
      if (paymentButtonType === ONE_CLICK_PAYMENT) return;
      const nextRoute = await getNextRoute();
      history.push(nextRoute);
    };

    const getTestId = () => {
      switch (paymentButtonType) {
        case PAYMENT:
          return 'payment-button-make-a-payment';
        case PAY_FULL_PLAN:
          return 'payment-button-pay-in-full';
        case ASSIST_PP:
          return 'payment-button-view-payment-plan-options';
        case ADD_OUTSIDE_BALANCE:
          return 'payment-button-add-outside-balance';
        case PP_BRING_CURRENT:
        case CATCH_UP_PROMPT:
        case NOTIFICATION_BANNER_CATCH_UP:
          return 'payment-button-bring-account-current';
        case PP_CONVERT:
        case PP_UPDATE_PAYMENT_METHOD:
        case NOTIFICATION_BANNER:
          return 'payment-button-payment-plan-convert';
      }
    };

    return (
      <StyledButton
        {...props}
        variant={variant || 'contained'}
        color={color || 'primary'}
        onClick={onPaymentClick}
        data-testid={getTestId()}
        role="payment-button"
      >
        {children}
      </StyledButton>
    );
  }
);

PaymentButton.propTypes = {
  ...SystemButton.propTypes,
  paymentButtonType: PropTypes.string.isRequired,
  sourceIsMessagesCenter: PropTypes.bool,
  billStatus: PropTypes.string
};

export default PaymentButton;
